import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "../../../../api/axios";
import { 
  ChartBarIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  TruckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { triggerProjectAccountingPdfDownload } from "../../accounting/project-accounting-print-content";
import { LaborSettlementDetails } from "../../accounting/LaborSettlementDetails";
import { CalculationInfoIcon } from "../../accounting/CalculationInfoIcon";
import { CalculationGuideDialog } from "../../accounting/CalculationGuideDialog";
import { signedFinancialColorClass } from "../../../../utils/signedFinancialColor";

// Cost Breakdown Chart Component
const CostBreakdownChart = ({ laborCosts, materialCosts, supplierInvoices, totalCosts, formatCurrency, t }) => {
  const size = 200;
  const center = size / 2;
  const radius = 80;
  const strokeWidth = 40;

  // Calculate percentages
  const laborPercent = totalCosts > 0 ? (laborCosts / totalCosts) * 100 : 0;
  const materialPercent = totalCosts > 0 ? (materialCosts / totalCosts) * 100 : 0;
  const supplierPercent = totalCosts > 0 ? (supplierInvoices / totalCosts) * 100 : 0;

  // Calculate angles for pie chart
  const circumference = 2 * Math.PI * radius;
  
  // Calculate stroke dash offsets (start from top, going clockwise)
  const laborLength = (laborPercent / 100) * circumference;
  const materialLength = (materialPercent / 100) * circumference;
  const supplierLength = (supplierPercent / 100) * circumference;
  
  // Offsets: each segment starts where the previous one ended
  const laborOffset = circumference - laborLength;
  const materialOffset = circumference - materialLength - laborLength;
  const supplierOffset = circumference - supplierLength - materialLength - laborLength;

  const colors = {
    labor: '#3B82F6', // blue
    material: '#10B981', // green
    supplier: '#F59E0B' // amber
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Pie Chart */}
      <div className="flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Labor costs - first segment */}
          {laborPercent > 0 && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={colors.labor}
              strokeWidth={strokeWidth}
              strokeDasharray={`${laborLength} ${circumference}`}
              strokeDashoffset={laborOffset}
              strokeLinecap="round"
            />
          )}
          {/* Material costs - second segment */}
          {materialPercent > 0 && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={colors.material}
              strokeWidth={strokeWidth}
              strokeDasharray={`${materialLength} ${circumference}`}
              strokeDashoffset={materialOffset}
              strokeLinecap="round"
            />
          )}
          {/* Supplier invoices - third segment */}
          {supplierPercent > 0 && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={colors.supplier}
              strokeWidth={strokeWidth}
              strokeDasharray={`${supplierLength} ${circumference}`}
              strokeDashoffset={supplierOffset}
              strokeLinecap="round"
            />
          )}
        </svg>
      </div>

      {/* Legend and Details */}
      <div className="space-y-4">
        <div className="space-y-3">
          {/* Labor Costs */}
          {laborPercent > 0 && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: colors.labor }}></div>
                <span className="text-sm font-medium text-gray-900">
                  {t("project.summary.labor_costs") || "Arbeidskostnader"}
                </span>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${signedFinancialColorClass(laborCosts, { neutralClass: "text-gray-900" })}`}>
                  {formatCurrency(laborCosts)}
                </div>
                <div className="text-xs text-gray-500">
                  {laborPercent.toFixed(1)}%
                </div>
              </div>
            </div>
          )}

          {/* Material Costs */}
          {materialPercent > 0 && (
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: colors.material }}></div>
                <span className="text-sm font-medium text-gray-900">
                  {t("project.summary.material_costs") || "Materialkostnader"}
                </span>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${signedFinancialColorClass(materialCosts, { neutralClass: "text-gray-900" })}`}>
                  {formatCurrency(materialCosts)}
                </div>
                <div className="text-xs text-gray-500">
                  {materialPercent.toFixed(1)}%
                </div>
              </div>
            </div>
          )}

          {/* Supplier Invoices */}
          {supplierPercent > 0 && (
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: colors.supplier }}></div>
                <span className="text-sm font-medium text-gray-900">
                  {t("project.summary.supplier_invoices") || "Leverandørfakturaer"}
                </span>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${signedFinancialColorClass(supplierInvoices, { neutralClass: "text-gray-900" })}`}>
                  {formatCurrency(supplierInvoices)}
                </div>
                <div className="text-xs text-gray-500">
                  {supplierPercent.toFixed(1)}%
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="border-t border-gray-200 pt-3 mt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">
              {t("project.summary.total_costs") || "Totale kostnader"}
            </span>
            <span className={`text-lg font-bold ${signedFinancialColorClass(totalCosts, { neutralClass: "text-gray-900" })}`}>
              {formatCurrency(totalCosts)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProjectSummaryTab = ({ project }) => {
  const { t, i18n } = useTranslation();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [materialGuideOpen, setMaterialGuideOpen] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setSummaryData(null);
        const response = await axios.get(
          `/projects/${project.id}/summary?include_modules=true`
        );
        if (response.data.success) {
          setSummaryData(response.data.data);
        } else {
          setError(t("project.summary.load_failed"));
        }
      } catch (err) {
        console.error("Error fetching project summary:", err);
        setError(t("project.summary.load_failed"));
      } finally {
        setLoading(false);
      }
    };

    if (project?.id) {
      fetchSummary();
    }
  }, [project?.id, t]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return `${numValue.toFixed(1)}%`;
  };

  const getTrendIcon = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    if (numValue > 0) return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
    if (numValue < 0) return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    return <MinusIcon className="h-4 w-4 text-gray-500" />;
  };

  const financialColor = (value) =>
    signedFinancialColorClass(value, { neutralClass: "text-gray-900" });

  const getTrendColor = (value) => financialColor(value);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    await triggerProjectAccountingPdfDownload({
      projectId: project.id,
      projectNumber: summaryData?.project_number,
      summary: summaryData,
      locale: i18n.language,
      includeModules: true,
      endpoint: `/projects/${project.id}/summary_pdf`,
      onError: (message) => alert(`${t("project.overview.export_pdf_error")}: ${message}`),
    });
    setPdfLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("project.summary.error.title") || "Feil ved lasting"}
        </h3>
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("project.summary.no_data.title") || "Ingen data tilgjengelig"}
        </h3>
        <p className="text-gray-500">
          {t("project.summary.no_data.description") || "Prosjektsummering vil bli generert når data er tilgjengelig"}
        </p>
      </div>
    );
  }

  const contractSum = summaryData.contract_sum || 0;
  const invoicedAmount = summaryData.invoiced_amount || 0;
  const totalCostsVal = summaryData.total_costs || 0;
  const customerInvoiceCount = summaryData.customer_invoice_count || 0;
  const hasCustomerInvoices = customerInvoiceCount > 0 || invoicedAmount !== 0;
  const profitVsContract =
    summaryData.profit_vs_contract ?? (contractSum - totalCostsVal);
  const marginVsContract =
    summaryData.margin_vs_contract ??
    (contractSum > 0 ? (profitVsContract / contractSum) * 100 : 0);
  const profitVsInvoiced =
    summaryData.profit_vs_invoiced ??
    summaryData.provisional_result ??
    invoicedAmount - totalCostsVal;
  const marginVsInvoiced =
    summaryData.margin_vs_invoiced ??
    (invoicedAmount > 0 ? (profitVsInvoiced / invoicedAmount) * 100 : 0);
  const invoicingRatePercent =
    contractSum > 0 ? (invoicedAmount / contractSum) * 100 : null;
  const costRateVsContract =
    contractSum > 0 ? (totalCostsVal / contractSum) * 100 : null;
  const costRateVsInvoiced =
    invoicedAmount > 0 ? (totalCostsVal / invoicedAmount) * 100 : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.summary.overview")}
        </h3>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {t("project.summary.last_updated") || "Sist oppdatert"}: {new Date(summaryData.last_updated).toLocaleString('no-NO')}
          </div>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {pdfLoading
              ? t("project.overview.export_pdf_loading")
              : t("accounting.project_summary.print.button")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.billing_plan.contract_sum")}
                  </dt>
                  <dd className={`text-lg font-medium ${financialColor(contractSum)}`}>
                    {formatCurrency(contractSum)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.summary.invoiced_amount")}
                  </dt>
                  <dd className={`text-lg font-medium ${financialColor(invoicedAmount)}`}>
                    {formatCurrency(invoicedAmount)}
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    {hasCustomerInvoices
                      ? t("project.summary.invoiced_ex_vat")
                      : t("project.summary.not_invoiced")}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClipboardDocumentListIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.summary.total_costs")}
                  </dt>
                  <dd className={`text-lg font-medium ${financialColor(totalCostsVal)}`}>
                    {formatCurrency(totalCostsVal)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {getTrendIcon(profitVsContract)}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.summary.profit_vs_contract")}
                  </dt>
                  <dd className={`text-lg font-medium ${getTrendColor(profitVsContract)}`}>
                    {formatCurrency(profitVsContract)}
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    {t("project.summary.margin")}: {formatPercentage(marginVsContract)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {getTrendIcon(profitVsInvoiced)}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.summary.profit_vs_invoiced")}
                  </dt>
                  <dd className={`text-lg font-medium ${getTrendColor(profitVsInvoiced)}`}>
                    {formatCurrency(profitVsInvoiced)}
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    {hasCustomerInvoices && invoicedAmount > 0
                      ? `${t("project.summary.margin")}: ${formatPercentage(marginVsInvoiced)}`
                      : t("project.summary.not_invoiced")}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income */}
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Inntekter
          </h4>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("project.summary.contract_sum") || "Kontraktsum"}</span>
              <span className={`text-sm font-medium ${financialColor(summaryData.contract_sum || 0)}`}>
                {formatCurrency(summaryData.contract_sum || 0)}
              </span>
            </div>
            {summaryData.contract_agreement_price > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("project.summary.contract_agreement_price")}</span>
                <span className={`text-sm font-medium ${financialColor(summaryData.contract_agreement_price || 0)}`}>
                  {formatCurrency(summaryData.contract_agreement_price || 0)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("project.summary.invoiced_amount") || "Fakturert beløp"}</span>
              <span className={`text-sm font-medium ${financialColor(invoicedAmount)}`}>
                {hasCustomerInvoices ? formatCurrency(invoicedAmount) : t("project.summary.not_invoiced")}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("project.billing_plan.remaining_amount") || "Innestående beløp"} (faktisk)</span>
              <span className={`text-sm font-medium ${financialColor(summaryData.remaining_amount || 0)}`}>
                {formatCurrency(summaryData.remaining_amount || 0)}
              </span>
            </div>
            {summaryData.expected_remaining_amount > 0 && contractSum > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Forventet innestående ({formatPercentage(summaryData.expected_remaining_percentage || 0)} av kontrakt)
                </span>
                <span className={`text-sm font-medium ${financialColor(summaryData.expected_remaining_amount || 0)}`}>
                  {formatCurrency(summaryData.expected_remaining_amount || 0)}
                </span>
              </div>
            )}
            {summaryData.expected_module_income > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("project.summary.expected_module_income") || "Forventet inntekt fra moduler"}</span>
                <span className={`text-sm font-medium ${financialColor(summaryData.expected_module_income || 0)}`}>
                  {formatCurrency(summaryData.expected_module_income || 0)}
                </span>
              </div>
            )}
            {contractSum > 0 && (
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{t("project.summary.remaining") || "Gjenstående"}</span>
                <span className={`text-sm font-medium ${financialColor(contractSum - invoicedAmount)}`}>
                  {formatCurrency(contractSum - invoicedAmount)}
                </span>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Costs */}
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Kostnader
          </h4>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">{t("project.summary.labor_costs")}</span>
              </div>
              <span className={`text-sm font-medium ${financialColor(summaryData.labor_costs || 0)}`}>
                {formatCurrency(summaryData.labor_costs || 0)}
              </span>
            </div>
            {summaryData.labor && (
              <div className="ml-6">
                <LaborSettlementDetails
                  labor={summaryData.labor}
                  t={t}
                  formatCurrency={formatCurrency}
                  translationPrefix="project.summary"
                />
              </div>
            )}
            <div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <ClipboardDocumentListIcon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-600">{t("project.summary.material_costs")}</span>
                  <CalculationInfoIcon
                    ariaLabel={t("project.summary.material_costs_info_aria")}
                    onClick={() => setMaterialGuideOpen(true)}
                  />
                </div>
                <span className={`text-sm font-medium ${financialColor(summaryData.material_costs || 0)}`}>
                  {formatCurrency(summaryData.material_costs || 0)}
                </span>
              </div>
              <p className="mt-1 ml-6 text-xs text-gray-500">
                {t("project.summary.material_costs_hint")}
              </p>
            </div>
            <CalculationGuideDialog
              open={materialGuideOpen}
              onClose={() => setMaterialGuideOpen(false)}
              titleKey="project.summary.material_costs"
              bodyKey="accounting.project_summary.methodology.material_costs"
            />
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <TruckIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">{t("project.summary.supplier_invoices")}</span>
              </div>
              <span className={`text-sm font-medium ${financialColor(summaryData.supplier_invoices || 0)}`}>
                {formatCurrency(summaryData.supplier_invoices || 0)}
              </span>
            </div>
            {summaryData.voucher_expenses > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("project.summary.voucher_expenses")}</span>
                <span className={`text-sm font-medium ${financialColor(summaryData.voucher_expenses || 0)}`}>
                  {formatCurrency(summaryData.voucher_expenses || 0)}
                </span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{t("project.summary.total_costs")}</span>
                <span className={`text-sm font-medium ${financialColor(summaryData.total_costs || 0)}`}>
                  {formatCurrency(summaryData.total_costs || 0)}
                </span>
              </div>
            </div>
            {summaryData.expected_module_costs > 0 && (
              <div className="flex justify-between items-center pt-1">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-xs text-gray-500 italic">
                    {t("project.summary.expected_module_costs") || "Forventet modulkostnader"}{" "}
                    ({t("project.summary.not_included_in_total") || "ikke inkludert i total"})
                  </span>
                </div>
                <span className={`text-xs italic ${financialColor(summaryData.expected_module_costs || 0)}`}>
                  {formatCurrency(summaryData.expected_module_costs || 0)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profitability Analysis */}
      <div className="bg-white shadow rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          Lønnsomhetsanalyse
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className={`text-2xl font-bold mb-2 ${financialColor(marginVsContract)}`}>
              {contractSum > 0 ? formatPercentage(marginVsContract) : "—"}
            </div>
            <div className="text-sm text-gray-600">
              {t("project.summary.margin_vs_contract")}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formatCurrency(profitVsContract)} {t("project.summary.of")}{" "}
              {formatCurrency(contractSum)}
            </div>
          </div>

          <div className="text-center">
            <div className={`text-2xl font-bold mb-2 ${hasCustomerInvoices && invoicedAmount > 0 ? financialColor(marginVsInvoiced) : "text-gray-900"}`}>
              {hasCustomerInvoices && invoicedAmount > 0
                ? formatPercentage(marginVsInvoiced)
                : "—"}
            </div>
            <div className="text-sm text-gray-600">
              {t("project.summary.margin_vs_invoiced")}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {hasCustomerInvoices ? (
                <>
                  {formatCurrency(profitVsInvoiced)} {t("project.summary.of")}{" "}
                  {formatCurrency(invoicedAmount)}
                </>
              ) : (
                t("project.summary.not_invoiced")
              )}
            </div>
          </div>
          
          <div className="text-center">
            <div className={`text-2xl font-bold mb-2 ${invoicingRatePercent != null ? financialColor(invoicingRatePercent) : "text-gray-900"}`}>
              {invoicingRatePercent != null
                ? formatPercentage(invoicingRatePercent)
                : "—"}
            </div>
            <div className="text-sm text-gray-600">{t("project.summary.invoicing_rate") || "Faktureringsgrad"}</div>
            <div className="text-xs text-gray-500 mt-1">
              {hasCustomerInvoices ? formatCurrency(invoicedAmount) : t("project.summary.not_invoiced")}
              {invoicingRatePercent != null ? (
                <>
                  {" "}
                  {t("project.summary.of")} {formatCurrency(contractSum)}
                </>
              ) : (
                <span className="text-gray-400"> ({t("project.summary.invoicing_rate_no_contract")})</span>
              )}
            </div>
          </div>
          
          <div className="text-center">
            <div className={`text-2xl font-bold mb-2 ${financialColor(costRateVsContract ?? costRateVsInvoiced ?? 0)}`}>
              {costRateVsContract != null
                ? formatPercentage(costRateVsContract)
                : costRateVsInvoiced != null
                  ? formatPercentage(costRateVsInvoiced)
                  : "—"}
            </div>
            <div className="text-sm text-gray-600">{t("project.summary.cost_rate") || "Kostnadsgrad"}</div>
            <div className="text-xs text-gray-500 mt-1">
              {formatCurrency(totalCostsVal)}{" "}
              {t("project.summary.of")}{" "}
              {formatCurrency(contractSum > 0 ? contractSum : invoicedAmount)}
              {contractSum <= 0 && invoicedAmount > 0 ? (
                <span className="text-gray-400"> ({t("project.summary.cost_rate_on_invoiced_hint")})</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Cost Breakdown Chart */}
      <div className="bg-white shadow rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          {t("project.summary.cost_breakdown") || "Kostnadsfordeling"}
        </h4>
        {summaryData.total_costs > 0 ? (
          <CostBreakdownChart 
            laborCosts={summaryData.labor_costs || 0}
            materialCosts={summaryData.material_costs || 0}
            supplierInvoices={summaryData.supplier_invoices || 0}
            totalCosts={summaryData.total_costs || 0}
            formatCurrency={formatCurrency}
            t={t}
          />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{t("project.summary.no_costs") || "Ingen kostnader registrert ennå"}</p>
          </div>
        )}
      </div>

      {/* Module Breakdown */}
      {summaryData.modules && summaryData.modules.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            {t("project.summary.module_breakdown") || "Moduloversikt"}
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            {t("project.summary.module_breakdown_description") || "Oversikt over alle moduler som påvirker prosjektsummeringen"}
          </p>
          <div className="space-y-4">
            {summaryData.modules.map((module, index) => {
              const getModuleIcon = () => {
                switch (module.module_type) {
                  case 'report':
                    return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
                  case 'work_order':
                    return <WrenchScrewdriverIcon className="h-5 w-5 text-purple-500" />;
                  case 'item_list':
                    return <ListBulletIcon className="h-5 w-5 text-green-500" />;
                  default:
                    return <ClipboardDocumentListIcon className="h-5 w-5 text-gray-500" />;
                }
              };

              const getModuleTypeLabel = () => {
                switch (module.module_type) {
                  case 'report':
                    return t("project.summary.module_type.report") || "Rapport";
                  case 'work_order':
                    return t("project.summary.module_type.work_order") || "Arbeidsordre";
                  case 'item_list':
                    return t("project.summary.module_type.item_list") || "Vareliste";
                  default:
                    return module.module_type;
                }
              };

              return (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getModuleIcon()}
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          {getModuleTypeLabel()}
                        </span>
                        {module.invoiced && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {t("project.summary.invoiced") || "Fakturert"}
                          </span>
                        )}
                        {(module.has_order || module.has_orders) && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {t("project.summary.has_order") || "Har ordre"}
                          </span>
                        )}
                      </div>
                      <Link
                        to={module.link}
                        className="text-base font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                      >
                        {module.display_title}
                      </Link>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Costs */}
                        <div className="bg-red-50 rounded-lg p-3">
                          <div className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            {t("project.summary.costs") || "Kostnader"}
                          </div>
                          <div className={`text-sm font-bold ${financialColor(module.costs.total || 0)}`}>
                            {formatCurrency(module.costs.total || 0)}
                          </div>
                          {module.costs.hours > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.hours") || "Timer"}:{" "}
                              <span className={financialColor(module.costs.hours)}>
                                {formatCurrency(module.costs.hours)}
                              </span>
                            </div>
                          )}
                          {module.material_costs > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.material") || "Materialer"}:{" "}
                              <span className={financialColor(module.material_costs)}>
                                {formatCurrency(module.material_costs)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Income */}
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            {t("project.summary.income") || "Inntekter"}
                          </div>
                          <div className={`text-sm font-bold ${financialColor(module.income.total || 0)}`}>
                            {formatCurrency(module.income.total || 0)}
                          </div>
                          {module.income.hours > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.hours") || "Timer"}:{" "}
                              <span className={financialColor(module.income.hours)}>
                                {formatCurrency(module.income.hours)}
                              </span>
                            </div>
                          )}
                          {module.income.agreed_price > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.agreed_price") || "Avtalt pris"}:{" "}
                              <span className={financialColor(module.income.agreed_price)}>
                                {formatCurrency(module.income.agreed_price)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Profit */}
                        <div className={`rounded-lg p-3 ${module.profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            {t("project.summary.profit") || "Fortjeneste"}
                          </div>
                          <div className={`text-sm font-bold ${financialColor(module.profit || 0)}`}>
                            {formatCurrency(module.profit || 0)}
                          </div>
                          {module.hours && module.hours.registered > 0 && (
                            <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                              <ClockIcon className="h-3 w-3" />
                              {(parseFloat(module.hours.registered) || 0).toFixed(1)} {t("project.summary.hours_short") || "t"}
                            </div>
                          )}
                          {module.price_lines && module.price_lines.length > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {module.price_lines.length} {t("project.summary.price_lines") || "prislinjer"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
